import { Switch, Route, Redirect, useLocation } from "wouter";
import { Suspense, lazy, useEffect, useState, Component, type ReactNode } from "react";
import { queryClient, ApiError } from "./lib/queryClient";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";

declare global {
  interface Window {
    __queryClient?: QueryClient;
  }
}
if (import.meta.env.DEV) {
  window.__queryClient = queryClient;
}
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/layout";
import ImpersonationBanner from "@/components/impersonation-banner";

const NotFound = lazy(() => import("@/pages/not-found"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Program = lazy(() => import("@/pages/program"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminPrograms = lazy(() => import("@/pages/admin-programs"));
const AdminPromoCodes = lazy(() => import("@/pages/admin-promo-codes"));
const AdminContent = lazy(() => import("@/pages/admin-content"));
const AdminMarkdownGuide = lazy(() => import("@/pages/admin-markdown-guide"));
const AdminPathways = lazy(() => import("@/pages/admin-pathways"));
const AdminUserLogs = lazy(() => import("@/pages/admin-user-logs"));
const AdminEmails = lazy(() => import("@/pages/admin-emails"));
const AdminTemplates = lazy(() => import("@/pages/admin-templates"));
const AdminCertifications = lazy(() => import("@/pages/admin-certifications"));
const AdvisorProgram = lazy(() => import("@/pages/advisor-program"));
const Advisor = lazy(() => import("@/pages/advisor"));
const AuditPrograms = lazy(() => import("@/pages/audit"));
const Conversations = lazy(() => import("@/pages/conversations"));
const Catalog = lazy(() => import("@/pages/catalog"));
const Redeem = lazy(() => import("@/pages/redeem"));
const Exam = lazy(() => import("@/pages/exam"));
const CertificatePage = lazy(() => import("@/pages/certificate"));
const VerifyPage = lazy(() => import("@/pages/verify"));
const Login = lazy(() => import("@/pages/login"));
const ProfilePage = lazy(() => import("@/pages/profile"));
const AssessmentHistory = lazy(() => import("@/pages/assessment-history"));
const IC8Assessment = lazy(() => import("@/pages/ic8-assessment"));
const IC8Report = lazy(() => import("@/pages/ic8-report"));
const AdminAssessments = lazy(() => import("@/pages/admin-assessments"));
const AdminAssessmentPreview = lazy(() => import("@/pages/admin-assessment-preview"));
const AdminArchivedTeams = lazy(() => import("@/pages/admin-archived-teams"));
const PurchasePage = lazy(() => import("@/pages/purchase"));
const PurchaseSuccessPage = lazy(() => import("@/pages/purchase-success"));
const PurchasePendingPage = lazy(() => import("@/pages/purchase-pending"));
const PurchaseSelectStartPage = lazy(() => import("@/pages/purchase-select-start"));
const SupportPage = lazy(() => import("@/pages/support"));
const DirectoryPage = lazy(() => import("@/pages/directory"));
const PublicProfilePage = lazy(() => import("@/pages/public-profile"));
const AdminTools = lazy(() => import("@/pages/admin-tools"));

function RouteFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white"
      data-testid="route-fallback"
    >
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540] mx-auto"></div>
        <p className="mt-4 text-[#0A2540] font-medium">Loading...</p>
      </div>
    </div>
  );
}

function PageFallback() {
  return (
    <div
      className="flex items-center justify-center w-full py-24"
      data-testid="page-fallback"
    >
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0A2540] mx-auto"></div>
        <p className="mt-3 text-[#0A2540] font-medium text-sm">Loading...</p>
      </div>
    </div>
  );
}

type UserRole = "participant" | "advisor" | "program_admin" | "content_admin" | "admin";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

/**
 * Page-scoped error boundary wrapping individual route components.
 * Catches render errors and shows a contained fallback panel.
 * Shows a contained "Something went wrong" panel rather than a blank screen,
 * and leaves the surrounding shell (nav, layout) intact so the user can
 * navigate away without a full page reload.
 */
function PageErrorFallback({ error }: { error?: Error }) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div
      className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center"
      data-testid="page-error-boundary"
      role="alert"
    >
      <h2 className="text-xl font-semibold text-[#0A2540] mb-2">
        Something went wrong
      </h2>
      <p className="text-gray-500 mb-4 max-w-sm">
        This page encountered an error. Please reload or navigate to another
        page.
      </p>
      {requestId && (
        <p className="text-xs text-gray-400 font-mono mb-4">
          Ref: {requestId}
        </p>
      )}
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="bg-[#0A2540] text-white px-4 py-2 rounded-md text-sm font-medium"
        data-testid="button-page-error-reload"
      >
        Reload page
      </button>
    </div>
  );
}

interface PageErrorBoundaryState {
  error: Error | null;
}

class PageErrorBoundary extends Component<{ children: ReactNode }, PageErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  render() {
    if (this.state.error) {
      return <PageErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

function AuthTimeoutFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white p-6"
      data-testid="auth-timeout-fallback"
      role="alert"
      aria-live="assertive"
    >
      <div className="text-center max-w-sm">
        <h1 className="text-xl font-bold text-[#0A2540] mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          We're having trouble connecting. Please refresh the page to try again.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-[#0A2540] text-white px-5 py-2 rounded-md font-medium"
          data-testid="button-auth-timeout-retry"
        >
          Refresh to try again
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading, authTimedOut } = useAuth();
  const [location] = useLocation();

  if (isLoading) {
    if (authTimedOut) return <AuthTimeoutFallback />;
    return <RouteFallback />;
  }

  if (!user) {
    // wouter's location is pathname-only; preserve any query string from window.location
    const search =
      typeof window !== "undefined" ? window.location.search : "";
    const fullPath = `${location}${search}`;
    return (
      <Redirect to={`/login?redirect=${encodeURIComponent(fullPath)}`} />
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Redirect to="/" />;
  }

  return <PageErrorBoundary>{children}</PageErrorBoundary>;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function isNoShellLocation(loc: string): boolean {
  if (loc === "/login" || loc === "/sign-in") return true;
  if (loc === "/sign-up") return true;
  if (loc.startsWith("/verify/")) return true;
  if (loc.startsWith("/purchase/")) return true;
  if (loc === "/assessment" || loc.startsWith("/assessment/")) return true;
  if (/^\/programs\/[^/]+\/(exam|certificate)\/?$/.test(loc)) return true;
  if (loc.startsWith("/u/")) return true;
  return false;
}

function NoShellRoutes() {
  return (
    <Switch>
      {/* Clerk's actor-token handoff returns here with __clerk_ticket.
          Render directly so the ticket query survives without a redirect. */}
      <Route path="/sign-in">
        <PageErrorBoundary>
          <Login />
        </PageErrorBoundary>
      </Route>
      <Route path="/login">
        <PageErrorBoundary>
          <Login />
        </PageErrorBoundary>
      </Route>
      <Route path="/sign-up">
        <PageErrorBoundary>
          <Login />
        </PageErrorBoundary>
      </Route>

      <Route path="/assessment/history">
        <ProtectedRoute>
          <AssessmentHistory />
        </ProtectedRoute>
      </Route>

      <Route path="/assessment/results/:id">
        <ProtectedRoute>
          <IC8Report />
        </ProtectedRoute>
      </Route>

      <Route path="/assessment">
        <ProtectedRoute>
          <IC8Assessment />
        </ProtectedRoute>
      </Route>

      <Route path="/programs/:id/exam">
        <ProtectedRoute>
          <Exam />
        </ProtectedRoute>
      </Route>

      <Route path="/programs/:id/certificate">
        <ProtectedRoute>
          <CertificatePage />
        </ProtectedRoute>
      </Route>

      <Route path="/verify/:certificateNumber">
        <PageErrorBoundary>
          <VerifyPage />
        </PageErrorBoundary>
      </Route>

      <Route path="/u/:slug">
        <PageErrorBoundary>
          <PublicProfilePage />
        </PageErrorBoundary>
      </Route>

      <Route path="/purchase/success">
        <PageErrorBoundary>
          <PurchaseSuccessPage />
        </PageErrorBoundary>
      </Route>
      <Route path="/purchase/select-start">
        <PageErrorBoundary>
          <PurchaseSelectStartPage />
        </PageErrorBoundary>
      </Route>
      <Route path="/purchase/pending">
        <PageErrorBoundary>
          <PurchasePendingPage />
        </PageErrorBoundary>
      </Route>
      <Route path="/purchase/:programId">
        <PageErrorBoundary>
          <PurchasePage />
        </PageErrorBoundary>
      </Route>

      <Route>
        <PageErrorBoundary>
          <NotFound />
        </PageErrorBoundary>
      </Route>
    </Switch>
  );
}

function InShellRoutes() {
  return (
    <Switch>
      <Route path="/">
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      </Route>

      <Route path="/program/:id">
        <ProtectedRoute>
          <Program />
        </ProtectedRoute>
      </Route>

      <Route path="/admin">
        <ProtectedRoute allowedRoles={["admin", "program_admin", "content_admin"]}>
          <Redirect to="/admin/users" />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/users">
        <ProtectedRoute allowedRoles={["admin", "program_admin", "content_admin"]}>
          <AdminUsers />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/programs">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminPrograms />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/promo-codes">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminPromoCodes />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/markdown-guide">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminMarkdownGuide />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/content/:programId/:dayId">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminContent />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/content/:programId">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminContent />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/content">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminContent />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/pathways">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminPathways />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/user-logs">
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminUserLogs />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/emails">
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminEmails />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/templates/:id">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminTemplates />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/templates">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminTemplates />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/certifications">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminCertifications />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/assessments">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminAssessments />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/assessments/preview/:styleKey">
        <ProtectedRoute allowedRoles={["admin", "content_admin"]}>
          <AdminAssessmentPreview />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/archived-teams">
        <ProtectedRoute allowedRoles={["admin", "content_admin", "program_admin"]}>
          <AdminArchivedTeams />
        </ProtectedRoute>
      </Route>

      <Route path="/admin/tools">
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTools />
        </ProtectedRoute>
      </Route>

      <Route path="/advisor">
        <ProtectedRoute allowedRoles={["advisor", "admin", "content_admin", "program_admin"]}>
          <Advisor />
        </ProtectedRoute>
      </Route>

      <Route path="/advisor/:id">
        <ProtectedRoute allowedRoles={["advisor", "admin", "content_admin", "program_admin"]}>
          <AdvisorProgram />
        </ProtectedRoute>
      </Route>

      <Route path="/audit">
        <ProtectedRoute>
          <AuditPrograms />
        </ProtectedRoute>
      </Route>

      <Route path="/conversations">
        <ProtectedRoute>
          <Conversations />
        </ProtectedRoute>
      </Route>

      <Route path="/catalog">
        <ProtectedRoute>
          <Catalog />
        </ProtectedRoute>
      </Route>

      <Route path="/redeem">
        <ProtectedRoute>
          <Redeem />
        </ProtectedRoute>
      </Route>

      <Route path="/profile">
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      </Route>

      <Route path="/support/:id">
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      </Route>

      <Route path="/support">
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      </Route>

      <Route path="/directory">
        <PageErrorBoundary>
          <DirectoryPage />
        </PageErrorBoundary>
      </Route>

      <Route>
        <PageErrorBoundary>
          <NotFound />
        </PageErrorBoundary>
      </Route>
    </Switch>
  );
}

function Router() {
  const [location] = useLocation();
  const noShell = isNoShellLocation(location);

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      {noShell ? (
        <Suspense fallback={<RouteFallback />}>
          <NoShellRoutes />
        </Suspense>
      ) : (
        <Layout>
          <Suspense fallback={<PageFallback />}>
            <InShellRoutes />
          </Suspense>
        </Layout>
      )}
    </>
  );
}

function ErrorFallback({ error }: { error?: Error }) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!requestId) return;
    navigator.clipboard.writeText(requestId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const mailtoHref = requestId
    ? `mailto:support@soullivingnow.com?subject=Error%20report&body=Error%20reference%3A%20${encodeURIComponent(requestId)}`
    : "mailto:support@soullivingnow.com?subject=Error%20report";

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white p-6"
      data-testid="error-fallback"
    >
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-[#0A2540] mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 mb-6">
          We've been notified and are looking into it. Please refresh the page
          to try again.
        </p>
        {requestId && (
          <div
            className="mb-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-md text-left"
            data-testid="error-request-id-box"
          >
            <p className="text-xs text-gray-500 mb-1">Error reference</p>
            <div className="flex items-center gap-2">
              <code
                className="text-xs font-mono text-gray-700 break-all flex-1"
                data-testid="text-error-request-id"
              >
                {requestId}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="text-xs text-[#0A2540] hover:underline whitespace-nowrap"
                data-testid="button-copy-error-request-id"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-[#0A2540] text-white px-4 py-2 rounded-md font-medium"
            data-testid="button-error-reload"
          >
            Reload page
          </button>
          <a
            href={mailtoHref}
            className="border border-[#0A2540] text-[#0A2540] px-4 py-2 rounded-md font-medium"
            data-testid="link-error-contact-support"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
